/**
 * Sentry Client-Side Configuration (T166)
 *
 * Initializes Sentry for browser-side error tracking and performance monitoring.
 * Includes Session Replay for debugging user issues.
 *
 * Features:
 * - Error tracking with source maps
 * - Performance monitoring with automatic instrumentation
 * - Session replay for error debugging
 * - User context setting for issue correlation
 */

import * as Sentry from '@sentry/remix';

/**
 * Sentry DSN from environment variable.
 * If not provided, Sentry will not initialize.
 */
const SENTRY_DSN = typeof window !== 'undefined'
  ? (window as typeof window & { ENV?: { SENTRY_DSN?: string } }).ENV?.SENTRY_DSN
  : undefined;

/**
 * Whether Sentry is enabled (DSN is provided).
 */
export const isSentryEnabled = (): boolean => {
  return Boolean(SENTRY_DSN);
};

/**
 * Initialize Sentry for client-side error tracking and performance monitoring.
 * Should be called in entry.client.tsx before React hydration.
 *
 * @example
 * ```typescript
 * import { initSentryClient } from '~/lib/sentry.client';
 *
 * initSentryClient();
 * startTransition(() => {
 *   hydrateRoot(document, <ClientApp />);
 * });
 * ```
 */
export function initSentryClient(): void {
  if (!SENTRY_DSN) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Sentry] Client-side initialization skipped: No DSN provided');
    }
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,

    // Performance Monitoring
    // Capture 10% of transactions for performance monitoring
    tracesSampleRate: 0.1,

    // Session Replay
    // Capture 10% of sessions for replay
    replaysSessionSampleRate: 0.1,
    // Capture 100% of sessions with errors for replay
    replaysOnErrorSampleRate: 1.0,

    // Trace propagation targets for distributed tracing
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/api\.yakinteractive\.com/,
      /^https:\/\/.*\.yakinteractive\.com/,
    ],

    // Integrations
    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration({}),
      // Session replay for debugging
      Sentry.replayIntegration({
        // Mask all text content for privacy
        maskAllText: true,
        // Block all media for privacy
        blockAllMedia: true,
      }),
    ],

    // Environment configuration
    environment: process.env.NODE_ENV || 'development',

    // Filter out known non-critical errors and scrub PII from events
    beforeSend(event) {
      // Ignore ResizeObserver errors (common and non-critical)
      if (event.exception?.values?.[0]?.value?.includes('ResizeObserver')) {
        return null;
      }

      // Scrub email addresses from exception messages and breadcrumb messages
      const emailPattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
      if (event.exception?.values) {
        for (const ex of event.exception.values) {
          if (ex.value) {
            ex.value = ex.value.replace(emailPattern, '[email]');
          }
        }
      }
      if (event.message) {
        event.message = event.message.replace(emailPattern, '[email]');
      }
      if (event.breadcrumbs) {
        for (const crumb of event.breadcrumbs) {
          if (crumb.message) {
            crumb.message = crumb.message.replace(emailPattern, '[email]');
          }
        }
      }

      // Scrub emails and sensitive query params from request URL and query_string.
      const sensitiveQueryParams = ['token', 'access_token', 'refresh_token', 'api_key', 'key'];
      const sensitiveParamPattern = new RegExp(
        `([?&])(${sensitiveQueryParams.join('|')})=([^&]*)`,
        'gi'
      );
      if (event.request?.url) {
        event.request.url = event.request.url
          .replace(emailPattern, '[email]')
          .replace(sensitiveParamPattern, '$1$2=[Filtered]');
      }
      if (event.request?.query_string !== undefined) {
        if (typeof event.request.query_string === 'string') {
          event.request.query_string = event.request.query_string
            .replace(emailPattern, '[email]')
            .replace(sensitiveParamPattern, '$1$2=[Filtered]');
        } else if (typeof event.request.query_string === 'object' && event.request.query_string !== null) {
          for (const param of sensitiveQueryParams) {
            if ((event.request.query_string as Record<string, string>)[param] !== undefined) {
              (event.request.query_string as Record<string, string>)[param] = '[Filtered]';
            }
          }
        }
      }

      // Remove sensitive request headers and cookies
      if (event.request?.headers) {
        const sensitiveHeaders = ['authorization', 'cookie', 'x-api-key'];
        for (const header of sensitiveHeaders) {
          if (event.request.headers[header]) {
            event.request.headers[header] = '[Filtered]';
          }
        }
      }
      if (event.request?.cookies) {
        delete event.request.cookies;
      }

      return event;
    },

    // Scrub PII from transaction events (URLs may contain tokens or emails)
    beforeSendTransaction(event) {
      // Skip health check transactions
      if (event.transaction?.includes('/health') || event.transaction?.includes('/ready')) {
        return null;
      }

      // Scrub emails and sensitive query params from request URL and query_string.
      const emailPattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
      const sensitiveQueryParams = ['token', 'access_token', 'refresh_token', 'api_key', 'key'];
      const sensitiveParamPattern = new RegExp(
        `([?&])(${sensitiveQueryParams.join('|')})=([^&]*)`,
        'gi'
      );
      if (event.request?.url) {
        event.request.url = event.request.url
          .replace(emailPattern, '[email]')
          .replace(sensitiveParamPattern, '$1$2=[Filtered]');
      }
      if (event.request?.query_string !== undefined) {
        if (typeof event.request.query_string === 'string') {
          event.request.query_string = event.request.query_string
            .replace(emailPattern, '[email]')
            .replace(sensitiveParamPattern, '$1$2=[Filtered]');
        } else if (typeof event.request.query_string === 'object' && event.request.query_string !== null) {
          for (const param of sensitiveQueryParams) {
            if ((event.request.query_string as Record<string, string>)[param] !== undefined) {
              (event.request.query_string as Record<string, string>)[param] = '[Filtered]';
            }
          }
        }
      }

      // Remove sensitive request headers and cookies
      if (event.request?.headers) {
        const sensitiveHeaders = ['authorization', 'cookie', 'x-api-key'];
        for (const header of sensitiveHeaders) {
          if (event.request.headers[header]) {
            event.request.headers[header] = '[Filtered]';
          }
        }
      }
      if (event.request?.cookies) {
        delete event.request.cookies;
      }

      return event;
    },

    // Debug mode in development
    debug: process.env.NODE_ENV === 'development',
  });

  if (process.env.NODE_ENV === 'development') {
    console.log('[Sentry] Client-side initialized successfully');
  }
}

/**
 * Capture an exception and send it to Sentry.
 *
 * @param error - The error to capture
 * @param context - Optional additional context for the error
 *
 * @example
 * ```typescript
 * try {
 *   await riskyOperation();
 * } catch (error) {
 *   captureException(error, {
 *     tags: { operation: 'riskyOperation' },
 *     extra: { userId: user.id }
 *   });
 * }
 * ```
 */
export function captureException(
  error: Error | unknown,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
    level?: Sentry.SeverityLevel;
  }
): string | undefined {
  if (!isSentryEnabled()) {
    console.error('[Sentry] Would capture exception:', error);
    return undefined;
  }

  return Sentry.captureException(error, {
    tags: context?.tags,
    extra: context?.extra,
    level: context?.level,
  });
}

/**
 * Capture a message and send it to Sentry.
 *
 * @param message - The message to capture
 * @param level - Optional severity level (default: 'info')
 * @param context - Optional additional context
 *
 * @example
 * ```typescript
 * captureMessage('User attempted invalid action', 'warning', {
 *   tags: { action: 'delete' },
 *   extra: { resourceId: '123' }
 * });
 * ```
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = 'info',
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
  }
): string | undefined {
  if (!isSentryEnabled()) {
    console.log(`[Sentry] Would capture message (${level}):`, message);
    return undefined;
  }

  return Sentry.captureMessage(message, {
    level,
    tags: context?.tags,
    extra: context?.extra,
  });
}

/**
 * Set user context for Sentry.
 * All subsequent events will be associated with this user.
 *
 * @param user - User information to set (null to clear)
 *
 * @example
 * ```typescript
 * setUser({
 *   id: user.id,
 *   email: user.email,
 *   organizationId: organization.id,
 * });
 * ```
 */
export function setUser(
  user: {
    id: string;
    email?: string;
    username?: string;
    organizationId?: string;
  } | null
): void {
  if (!isSentryEnabled()) {
    console.log('[Sentry] Would set user:', user);
    return;
  }

  if (user === null) {
    Sentry.setUser(null);
  } else {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username,
    });

    // Set organization context as a tag
    if (user.organizationId) {
      Sentry.setTag('organizationId', user.organizationId);
    }
  }
}

/**
 * Set a custom tag that will be attached to all subsequent events.
 *
 * @param key - Tag key
 * @param value - Tag value
 *
 * @example
 * ```typescript
 * setTag('release', 'v1.2.3');
 * setTag('feature', 'episode-editor');
 * ```
 */
export function setTag(key: string, value: string): void {
  if (!isSentryEnabled()) {
    console.log(`[Sentry] Would set tag: ${key}=${value}`);
    return;
  }

  Sentry.setTag(key, value);
}

/**
 * Add a breadcrumb to the Sentry trail.
 * Breadcrumbs help trace the sequence of events leading to an error.
 *
 * @param breadcrumb - Breadcrumb data
 *
 * @example
 * ```typescript
 * addBreadcrumb({
 *   category: 'navigation',
 *   message: 'User navigated to /episodes',
 *   level: 'info',
 * });
 * ```
 */
export function addBreadcrumb(breadcrumb: {
  category?: string;
  message?: string;
  level?: Sentry.SeverityLevel;
  data?: Record<string, unknown>;
}): void {
  if (!isSentryEnabled()) {
    console.log('[Sentry] Would add breadcrumb:', breadcrumb);
    return;
  }

  Sentry.addBreadcrumb(breadcrumb);
}

/**
 * Start a new performance span for measuring custom operations.
 *
 * @param name - Name of the operation
 * @param op - Operation type (e.g., 'http.client', 'db.query')
 * @returns A span object with end() method
 *
 * @example
 * ```typescript
 * const span = startSpan('fetchEpisodes', 'http.client');
 * try {
 *   await fetchEpisodes();
 * } finally {
 *   span.end();
 * }
 * ```
 */
export function startSpan(
  name: string,
  op: string
): { end: () => void } {
  if (!isSentryEnabled()) {
    return { end: () => {} };
  }

  const span = Sentry.startInactiveSpan({ name, op });

  return {
    end: () => span?.end(),
  };
}

/**
 * Re-export Sentry types for convenience.
 */
export type { SeverityLevel } from '@sentry/remix';
